import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Building, User, FileText, CheckCircle, Plus, Minus, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { companyInfo } from '../mock';
import axios from 'axios';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Service options for dropdown
const SERVICE_OPTIONS = [
  { value: 'lorry-crane-20', label: 'Lorry Crane - 20 Ton' },
  { value: 'lorry-crane-25', label: 'Lorry Crane - 25 Ton' },
  { value: 'lorry-crane-40', label: 'Lorry Crane - 40 Ton' },
  { value: 'lorry-crane-50', label: 'Lorry Crane - 50 Ton' },
  { value: 'lorry-crane-55', label: 'Lorry Crane - 55 Ton (With Winch)' },
  { value: 'lorry-crane-75', label: 'Lorry Crane - 75 Ton' },
  { value: 'lorry-crane-75-flyjib', label: 'Lorry Crane - 75 Ton (With Fly-Jib)' },
  { value: 'lorry-crane-100', label: 'Lorry Crane - 100 Ton' },
  { value: 'lorry-crane-100-flyjib', label: 'Lorry Crane - 100 Ton (With Fly-Jib)' },
  { value: 'trailer-40ft-34t', label: '40ft Flatbed Trailer (34-tons)' },
  { value: 'trailer-40ft-80t', label: '40ft Flatbed Trailer (80-tons)' },
  { value: 'trailer-lowbed', label: '40ft Lowbed Trailer' },
  { value: 'trailer-lowbed-ramp', label: '40ft Lowbed Trailer (Auto Ramp)' },
  { value: 'tailgate-14', label: '14FT Box Lorry' },
  { value: 'tailgate-24', label: '24FT Box Lorry' },
  { value: 'manpower', label: 'Manpower' },
  { value: 'other', label: 'Other (Please specify in details)' },
];

export const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Company Details
    company_name: '',
    company_contact: '',
    company_email: '',
    company_address: '',
    // Order Person Details
    order_person_name: '',
    order_person_phone: '',
    order_person_email: '',
    // Request Details - Multiple items
    request_items: [{ service_type: '', quantity: '1', details: '' }],
    // Additional info
    additional_requirements: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.request_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({ ...prev, request_items: newItems }));
  };

  const addRequestItem = () => {
    if (formData.request_items.length < 5) {
      setFormData(prev => ({
        ...prev,
        request_items: [...prev.request_items, { service_type: '', quantity: '1', details: '' }]
      }));
    }
  };

  const removeRequestItem = (index) => {
    if (formData.request_items.length > 1) {
      const newItems = formData.request_items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, request_items: newItems }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Format request items for submission
      const itemsSummary = formData.request_items
        .filter(item => item.service_type)
        .map((item, i) => {
          const serviceLabel = SERVICE_OPTIONS.find(s => s.value === item.service_type)?.label || item.service_type;
          return `Item ${i + 1}: ${serviceLabel} x${item.quantity}${item.details ? ` - ${item.details}` : ''}`;
        })
        .join('\n');

      const subject = formData.request_items.length === 1 
        ? `Quotation Request - ${SERVICE_OPTIONS.find(s => s.value === formData.request_items[0].service_type)?.label || 'Service'}`
        : `Quotation Request - ${formData.request_items.length} Items`;

      const message = `REQUESTED SERVICES:\n${itemsSummary}\n\nADDITIONAL REQUIREMENTS:\n${formData.additional_requirements || 'None'}`;

      const submitData = {
        company_name: formData.company_name,
        company_contact: formData.company_contact,
        company_email: formData.company_email,
        company_address: formData.company_address,
        order_person_name: formData.order_person_name,
        order_person_phone: formData.order_person_phone,
        order_person_email: formData.order_person_email,
        subject: subject,
        message: message
      };

      const response = await axios.post(`${BACKEND_URL}/api/quotations/`, submitData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({
          company_name: '',
          company_contact: '',
          company_email: '',
          company_address: '',
          order_person_name: '',
          order_person_phone: '',
          order_person_email: '',
          request_items: [{ service_type: '', quantity: '1', details: '' }],
          additional_requirements: ''
        });
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Something went wrong. Please try again.');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-stone-100 via-white to-stone-50">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get a Quotation
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Fill out the form below and we'll respond with a quotation within 24 hours
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gold/15 rounded-lg flex items-center justify-center mb-4">
                    <Phone className="text-burgundy" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                  <a href={`tel:${companyInfo.phone}`} className="text-burgundy hover:underline">
                    {companyInfo.phone}
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gold/15 rounded-lg flex items-center justify-center mb-4">
                    <Mail className="text-burgundy" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                  <a href={`mailto:${companyInfo.email}`} className="text-burgundy hover:underline break-all">
                    {companyInfo.email}
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gold/15 rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="text-burgundy" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
                  <p className="text-gray-600">{companyInfo.address}</p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gold/15 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="text-burgundy" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Business Hours</h3>
                  <div className="text-gray-600 space-y-1">
                    <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                    <p>Saturday: 8:00 AM - 2:00 PM</p>
                    <p>Sunday: By Appointment</p>
                    <p className="text-burgundy font-medium mt-2">24/7 Emergency Service</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quotation Form */}
            <div className="lg:col-span-2">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">Request a Quotation</CardTitle>
                  <CardDescription>Please provide your details and requirements below</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* Section 1: Company Details */}
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Building size={20} className="text-burgundy" />
                        Company Details
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="companyName" className="flex items-center gap-2">
                            <Building size={16} />
                            Company Name *
                          </Label>
                          <Input
                            id="companyName"
                            placeholder="ABC Construction Pte Ltd"
                            value={formData.company_name}
                            onChange={(e) => handleChange('company_name', e.target.value)}
                            required
                            data-testid="company-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="companyContact" className="flex items-center gap-2">
                            <Phone size={16} />
                            Company Contact *
                          </Label>
                          <Input
                            id="companyContact"
                            type="tel"
                            placeholder="6512345678"
                            value={formData.company_contact}
                            onChange={(e) => handleChange('company_contact', e.target.value)}
                            required
                            data-testid="company-contact-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="companyEmail" className="flex items-center gap-2">
                            <Mail size={16} />
                            Company Email *
                          </Label>
                          <Input
                            id="companyEmail"
                            type="email"
                            placeholder="info@company.com"
                            value={formData.company_email}
                            onChange={(e) => handleChange('company_email', e.target.value)}
                            required
                            data-testid="company-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="companyAddress" className="flex items-center gap-2">
                            <MapPin size={16} />
                            Company Address
                          </Label>
                          <Input
                            id="companyAddress"
                            placeholder="123 Main Street, Singapore 123456"
                            value={formData.company_address}
                            onChange={(e) => handleChange('company_address', e.target.value)}
                            data-testid="company-address-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Order Person Details */}
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <User size={20} className="text-burgundy" />
                        Order Person Details
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="orderPersonName" className="flex items-center gap-2">
                            <User size={16} />
                            Contact Name *
                          </Label>
                          <Input
                            id="orderPersonName"
                            placeholder="John Doe"
                            value={formData.order_person_name}
                            onChange={(e) => handleChange('order_person_name', e.target.value)}
                            required
                            data-testid="order-person-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="orderPersonPhone" className="flex items-center gap-2">
                            <Phone size={16} />
                            Contact Number *
                          </Label>
                          <Input
                            id="orderPersonPhone"
                            type="tel"
                            placeholder="98765432"
                            value={formData.order_person_phone}
                            onChange={(e) => handleChange('order_person_phone', e.target.value)}
                            required
                            data-testid="order-person-phone-input"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="orderPersonEmail" className="flex items-center gap-2">
                            <Mail size={16} />
                            Contact Email *
                          </Label>
                          <Input
                            id="orderPersonEmail"
                            type="email"
                            placeholder="john@company.com"
                            value={formData.order_person_email}
                            onChange={(e) => handleChange('order_person_email', e.target.value)}
                            required
                            data-testid="order-person-email-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Request Items */}
                    <div className="border-t pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Truck size={20} className="text-burgundy" />
                          Service Request
                        </h3>
                        {formData.request_items.length < 5 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addRequestItem}
                            className="text-burgundy"
                          >
                            <Plus size={16} className="mr-1" />
                            Add Item
                          </Button>
                        )}
                      </div>

                      <div className="space-y-4">
                        {formData.request_items.map((item, index) => (
                          <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-gray-700">
                                Item {index + 1}
                                {index > 0 && (
                                  <span className="ml-2 text-xs bg-gold/15 text-burgundy px-2 py-0.5 rounded">
                                    Additional
                                  </span>
                                )}
                              </span>
                              {index > 0 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeRequestItem(index)}
                                  className="text-red-600 h-8 w-8 p-0"
                                >
                                  <Minus size={16} />
                                </Button>
                              )}
                            </div>
                            
                            <div className="grid md:grid-cols-12 gap-3">
                              <div className="md:col-span-6 space-y-2">
                                <Label>Service Type *</Label>
                                <Select
                                  value={item.service_type}
                                  onValueChange={(value) => handleItemChange(index, 'service_type', value)}
                                  required
                                >
                                  <SelectTrigger data-testid={`service-type-${index}`}>
                                    <SelectValue placeholder="Select service type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SERVICE_OPTIONS.map(option => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="md:col-span-2 space-y-2">
                                <Label>Qty *</Label>
                                <Select
                                  value={item.quantity}
                                  onValueChange={(value) => handleItemChange(index, 'quantity', value)}
                                  required
                                >
                                  <SelectTrigger data-testid={`quantity-${index}`}>
                                    <SelectValue placeholder="Qty" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="md:col-span-4 space-y-2">
                                <Label>Details (Optional)</Label>
                                <Input
                                  placeholder="e.g., dates, location"
                                  value={item.details}
                                  onChange={(e) => handleItemChange(index, 'details', e.target.value)}
                                  data-testid={`details-${index}`}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {formData.request_items.length >= 5 && (
                        <p className="text-sm text-gray-500 mt-2">Maximum 5 items per quotation request</p>
                      )}
                    </div>

                    {/* Section 4: Additional Requirements */}
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <FileText size={20} className="text-burgundy" />
                        Additional Requirements
                      </h3>
                      <div className="space-y-2">
                        <Label htmlFor="additionalRequirements">
                          Any other details or special requirements
                        </Label>
                        <Textarea
                          id="additionalRequirements"
                          placeholder="Please provide any additional information such as:
• Job location and site access details
• Expected dates and duration
• Cargo weight and dimensions
• Special requirements or questions"
                          value={formData.additional_requirements}
                          onChange={(e) => handleChange('additional_requirements', e.target.value)}
                          rows={5}
                          data-testid="additional-requirements-input"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-burgundy hover:bg-burgundy-light text-white"
                        disabled={isSubmitting}
                        data-testid="submit-quotation-btn"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Submitting...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <CheckCircle size={20} />
                            Submit Quotation Request
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="aspect-[21/9] bg-gray-200 rounded-2xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.677177!2d103.902!3d1.405!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMcKwMjQnMTguMCJOIDEwM8KwNTQnMDcuMiJF!5e0!3m2!1sen!2ssg!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="NJK Logistic Pte Ltd Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
